import { redirect } from '@tanstack/react-router';
import { readAuthToken, LOGIN_PATH } from '@noor/api';

/**
 * Auth guard for every screen that reads the merchant API.
 *
 * Without it a signed-out visit RENDERS the screen and lets its queries fail,
 * which surfaces as a DATA error rather than a session one. The Menu screen
 * showed "Не удалось загрузить меню" for what was really a missing session,
 * and the network tab looked healthy because its two catalog calls do not fail
 * alike when signed out: `products/by-restaurant` is public and answers 200,
 * while `product-categories/by-merchant` is token-scoped and answers 401. The
 * `Promise.all` in `menuApi.get` rejects on that 401.
 *
 * A 401 does eventually reach `handleUnauthorized()` → `location.replace()`,
 * so the page was never permanently stuck — but that redirect is a separate,
 * asynchronous turn AFTER the query settles, and the query only settles once
 * `DEFAULT_QUERY_RETRY` (2) retries are exhausted. Across three internet
 * round-trips the error state is painted and sits there, which is the whole
 * user-visible bug.
 *
 * Redirecting on the ROUTE — before the component mounts and any query fires —
 * removes both halves: no request goes out unauthenticated, so no screen has to
 * translate a 401 into copy of its own and there is no window to paint.
 *
 * `readAuthToken()` only proves a token EXISTS, not that it is still valid; an
 * expired one still 401s, and `@noor/api`'s response interceptor owns that case
 * (refresh once, else `handleUnauthorized()` → `/login`). This guard is the
 * cheap synchronous half — it catches the common "no session at all" visit
 * without a round-trip.
 *
 * `redirect()` is TanStack Router's control-flow throw, not an Error.
 */
export function requireAuth(): void {
  if (readAuthToken()) return;
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({ to: LOGIN_PATH });
}
